import EmailIcons from '@/icons/Socials/Email'
import FacebookIcons from '@/icons/Socials/Facebook'
import InstagramIcons from '@/icons/Socials/Instagram'
import LinkedInIcons from '@/icons/Socials/LinkedIn'
import LocationIcons from '@/icons/Socials/Location'
import PhoneIcons from '@/icons/Socials/Phone'
import TelegramIcons from '@/icons/Socials/Telegram'
import TiktokIcons from '@/icons/Socials/Tiktok'
import TwitterIcons from '@/icons/Socials/Twitter'
import WhatsAppIcons from '@/icons/Socials/WhatsApp'
import YoutubeIcons from '@/icons/Socials/Youtube'
import EditIcons from '@/icons/EditIcons'
import { copyToClipboard } from '@/utils/function'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import { VCard } from '../../types/response'
import {
    buildVCardActionItems,
    getVCardPublicUrl,
    getVCardVcfDownloadUrl,
    isMobileDevice,
    VCardActionItem,
} from '../../utils/vcard-public'
import VCardQrCode from './VCardQrCode'

const VNETWORK_AVATAR_FALLBACK =
    'https://static.vncdn.vn/vnetwork.vn/pub/websites/uploads/1/icon.png'

type VCardPublicViewProps = {
    vcard: VCard
    uuid: string
    mode?: 'preview' | 'public'
    showEdit?: boolean
    onEdit?: () => void
    compact?: boolean
}

const actionIcon = (key: string, size = 22): ReactNode => {
    switch (key.toLowerCase()) {
        case 'address':
            return (
                <LocationIcons
                    size={size}
                    style={{ filter: 'brightness(0) invert(1)' }}
                />
            )
        case 'phone':
            return <PhoneIcons size={size} />
        case 'email':
            return <EmailIcons size={size} />
        case 'linkedin':
            return <LinkedInIcons size={size} />
        case 'facebook':
            return <FacebookIcons size={size} />
        case 'instagram':
            return <InstagramIcons size={size} />
        case 'whatsapp':
            return <WhatsAppIcons size={size} />
        case 'telegram':
            return <TelegramIcons size={size} />
        case 'twitter':
        case 'x':
            return <TwitterIcons size={size} />
        case 'youtube':
            return <YoutubeIcons size={size} />
        case 'tiktok':
            return <TiktokIcons size={size} />
        default:
            return (
                <span className="text-xs font-bold uppercase text-white">
                    {key.slice(0, 2)}
                </span>
            )
    }
}

const VCardPublicView: React.FC<VCardPublicViewProps> = ({
    vcard,
    uuid,
    mode = 'public',
    showEdit = false,
    onEdit,
    compact = false,
}) => {
    const { t } = useTranslation('vcard')
    const actions = buildVCardActionItems(vcard)
    const qrSize = compact ? 160 : 220
    // Preview (employee) always shows QR. Public (customer) shows QR only on
    // desktop so they can scan it with their phone; hidden on mobile.
    const showQr = Boolean(uuid) && (mode === 'preview' || !isMobileDevice())

    const handleActionClick = (item: VCardActionItem) => {
        if (item.href) {
            if (item.external) {
                window.open(item.href, '_blank', 'noopener,noreferrer')
            } else {
                window.location.href = item.href
            }
            return
        }
        copyToClipboard(item.value, t)
    }

    const handleSaveContact = () => {
        if (!uuid) return
        window.open(getVCardVcfDownloadUrl(uuid), '_self')
    }

    const handleCopyLink = () => {
        if (!uuid) return
        copyToClipboard(getVCardPublicUrl(uuid), t)
    }

    return (
        <div className="w-full mx-auto max-w-md">
            <div className="rounded-2xl overflow-hidden shadow-lg bg-[#FFF1F2]">
                <div className="relative bg-[#C8102E] px-5 pt-5 pb-12">
                    {showEdit && onEdit && (
                        <IconButton
                            size="small"
                            className="!absolute right-3 bottom-3 !bg-white/90 hover:!bg-white z-20"
                            onClick={onEdit}
                        >
                            <EditIcons className="w-4 h-4 text-primary" />
                        </IconButton>
                    )}
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-white font-extrabold text-[28px] leading-[1.1]">
                                Leading
                                <br />
                                Cloud
                                <br />
                                Security
                            </h2>
                            <span className="text-white/70 text-xs mt-2 block">
                                www.vnetwork.vn
                            </span>
                        </div>
                        <img
                            src="/images/vnetwork-logo.png"
                            alt="VNETWORK - safe and saved"
                            className="w-40 h-auto object-contain mt-6"
                            style={{ filter: 'brightness(0) invert(1)' }}
                        />
                    </div>
                </div>

                <div className="px-5 pb-6 -mt-12 flex flex-col items-center relative z-10">
                    <div className="w-28 h-28 rounded-full ring-4 ring-[#FFF1F2] overflow-hidden shadow-md bg-white">
                        <img
                            src={vcard.avatar || VNETWORK_AVATAR_FALLBACK}
                            alt={vcard.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                ;(e.target as HTMLImageElement).src =
                                    VNETWORK_AVATAR_FALLBACK
                            }}
                        />
                    </div>

                    <Typogaphy
                        size="large"
                        className="mt-4 font-bold text-center uppercase tracking-wide text-gray-900"
                    >
                        {(vcard.name || '').trim().toUpperCase()}
                    </Typogaphy>

                    {vcard.position && (
                        <Typogaphy
                            size="small"
                            className="text-gray-700 text-center uppercase tracking-wide mt-1"
                        >
                            {vcard.position}
                        </Typogaphy>
                    )}

                    {vcard.description && (
                        <Typogaphy
                            size="small"
                            className="text-gray-500 mt-2 text-center italic"
                        >
                            {vcard.description}
                        </Typogaphy>
                    )}

                    {showQr && (
                        <div className="mt-5">
                            <VCardQrCode
                                value={getVCardPublicUrl(uuid)}
                                size={qrSize}
                            />
                        </div>
                    )}

                    {actions.length > 0 && (
                        <div className="mt-5 grid grid-cols-3 gap-3 w-full">
                            {actions.slice(0, 6).map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    onClick={() => handleActionClick(item)}
                                    className="flex flex-col items-center gap-2 group"
                                >
                                    <span className="w-14 h-14 rounded-xl bg-[#C8102E] text-white flex items-center justify-center shadow-sm transition group-hover:scale-105">
                                        {actionIcon(item.key)}
                                    </span>
                                    <span className="text-[11px] font-semibold text-gray-700 uppercase text-center leading-tight">
                                        {item.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {mode === 'preview' ? (
                        <button
                            type="button"
                            onClick={handleCopyLink}
                            className="mt-6 w-full rounded-xl bg-[#C8102E] text-white py-3 text-sm font-bold uppercase tracking-wide shadow-md hover:opacity-95 transition"
                        >
                            {t('copy_link')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSaveContact}
                            className="mt-6 w-full rounded-xl bg-[#C8102E] text-white py-3 text-sm font-bold uppercase tracking-wide shadow-md hover:opacity-95 transition"
                        >
                            {t('save_contact')}
                        </button>
                    )}

                    <div className="mt-4 text-[11px] text-[#B9B9B9] text-center">
                        {t('footer_branding')}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default VCardPublicView
